import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { AssignIcon, IconButton, Select, Tooltip } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import i18n from "~/i18n";
import { useAssigneeTitle } from "./useAssigneeTitle.js";
import AvatarContainer from "../Users/AvatarContainer.jsx";

const Target = forwardRef(({ onClick, name, email, assigneeId, title, open, className, style, ...rest }, ref) => {
  return (
    <Tooltip title={title}>
      <IconButton
        {...rest}
        variant="text gray"
        type="button"
        data-testid={`user-${assigneeId}`}
        active={open}
        onClick={onClick}
        ref={ref}
        className={classnames(className, {
          open,
          no_assignee_target: assigneeId <= 0,
        })}
        style={style}
      >
        {name || (assigneeId && assigneeId > 0) ? (
          <AvatarContainer
            id={assigneeId}
            name={name}
            email={email}
          />
        ) : (
          <AssignIcon />
        )}
      </IconButton>
    </Tooltip>
  );
});

Target.propTypes = {
  onClick: PropTypes.func,
  assigneeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  open: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  name: PropTypes.string,
  email: PropTypes.string,
};

Target.displayName = "Target";

export const AssigneePicker = ({
  onChange,
  assigneeId,
  fakeAssigneeName,
  options,
  position,
  userName,
  userEmail,
  placeholder,
  noDefaultValue = false,
  ...rest
}) => {
  const { t } = useTranslation();

  const title = useAssigneeTitle(assigneeId, fakeAssigneeName);

  return (
    <Select
      target={
        <Target
          {...rest}
          name={userName}
          email={userEmail}
          assigneeId={assigneeId}
          title={title}
        />
      }
      defaultValue={!noDefaultValue ? `${t("No Assignee")}...` : undefined}
      placeholder={placeholder}
      noResultText={t("No result")}
      selected={assigneeId > 0 ? assigneeId : undefined}
      options={options}
      onChange={onChange}
      position={position}
      forceCloseMenu
      disabledInternalSort
    />
  );
};

AssigneePicker.propTypes = {
  assigneeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  fakeAssigneeName: PropTypes.string,
  options: PropTypes.array,
  position: PropTypes.string,
  noDefaultValue: PropTypes.bool,
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  placeholder: PropTypes.string,
};

AssigneePicker.defaultProps = {
  placeholder: i18n.t("Choose Assignee"),
};
